import type {
  Diagram,
  EMRALD_Model,
  MainItemType,
} from '../../../types/EMRALD_Model';
import type { ModelItem } from '../../../types/ModelUtils';
import type { AccordionMenuItemType } from './types/AccordionMenuItems';
import { Menu, MenuItem } from '@mui/material';
import List from '@mui/material/List';
import Typography from '@mui/material/Typography';
import { type MouseEvent, type SyntheticEvent, useState } from 'react';
import { useAlertContext } from '../../../contexts/AlertContext';
import { useWindowContext } from '../../../contexts/WindowContext';
import { upgradeModel } from '../../../utils/Upgrades/upgrade';
import { ActionForm } from '../../forms/ActionForm/ActionForm';
import { ActionFormContextProvider } from '../../forms/ActionForm/ActionFormContext';
import { DiagramForm } from '../../forms/DiagramForm/DiagramForm';
import { EventForm } from '../../forms/EventForm/EventForm';
import { EventFormContextProvider } from '../../forms/EventForm/EventFormContext';
import { ExtSimForm } from '../../forms/ExtSimForm/ExtSimForm';
import { ImportForm } from '../../forms/ImportForm/ImportForm';
import { LogicNodeForm } from '../../forms/LogicNodeForm/LogicNodeForm';
import { LogicNodeFormContextProvider } from '../../forms/LogicNodeForm/LogicNodeFormContext';
import { VariableForm } from '../../forms/VariableForm/VariableForm';
import { VariableFormContextProvider } from '../../forms/VariableForm/VariableFormContext';
import { AccordionMenuItems } from './AccordionMenuItems';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
} from './StyledComponents/StyledComponents';

interface MenuAccordionProps {
  panels: AccordionMenuItemType[];
  group: string;
  setAccordionGroupOpen: (value: boolean) => void;
  bothAccordionsOpen: boolean;
  onDiagramChange: (diagram: Diagram) => void;
  componentGroup?: string;
  handleDelete?: (itemToDelete: ModelItem, itemType: MainItemType) => void;
}

export const MenuAccordion: React.FC<MenuAccordionProps> = ({
  panels,
  setAccordionGroupOpen,
  bothAccordionsOpen,
  onDiagramChange,
  componentGroup,
  handleDelete,
}) => {
  const { addWindow } = useWindowContext();
  const { showAlert } = useAlertContext();
  const [expandedPanel, setExpandedPanel] = useState<string>('');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [notValidModel, setNotValidModel] = useState<boolean>(false);
  const [pastedModel, setPastedModel] = useState<EMRALD_Model | null>(null);
  const [accordionPanel, setAccordionPanel] = useState<string>('');

  const isEmraldModel = (clipboardData: string) => {
    if (typeof clipboardData !== 'string') {
      return false;
    }

    try {
      const parsedModel = JSON.parse(clipboardData) as EMRALD_Model | null;
      const upgradedModel = upgradeModel(clipboardData);
      if (
        (parsedModel
          && Object.prototype.hasOwnProperty.call(parsedModel, 'emraldVersion'))
        || (parsedModel && !upgradedModel)
      ) {
        setPastedModel(parsedModel);
      } else if (!parsedModel && upgradedModel) {
        setPastedModel(upgradedModel);
      }
      return (
        Object.prototype.hasOwnProperty.call(parsedModel, 'emraldVersion')
        || ((upgradedModel
          && Object.prototype.hasOwnProperty.call(
            upgradedModel,
            'emraldVersion',
          ))
          ?? false)
      );
    } catch {
      return false;
    }
  };

  const handleChange
    = (panel: string) => (_event: SyntheticEvent, isExpanded: boolean) => {
      setExpandedPanel(isExpanded ? panel : '');
      setAccordionGroupOpen(isExpanded);
    };

  const handleAccordionContextMenu = async (
    event: MouseEvent<HTMLDivElement>,
    panel: string,
  ) => {
    if (
      componentGroup !== 'global'
      && panel !== 'Diagrams'
      && panel !== 'Logic Tree'
      && panel !== 'External Sims'
      && panel !== 'Variables'
    ) {
      return;
    }
    event.preventDefault();
    setAccordionPanel(panel);
    setAnchorEl(event.currentTarget);
    if (panel === 'Diagrams') {
      event.preventDefault();
      setAnchorEl(event.currentTarget);
      const pastedData = await navigator.clipboard.readText();
      isEmraldModel(pastedData)
        ? setNotValidModel(false)
        : setNotValidModel(true);
    }
  };

  const handleMenuItemClick = (e: MouseEvent<HTMLElement>) => {
    switch (accordionPanel) {
      case 'Diagrams': {
        if (e.currentTarget.textContent === 'Paste') {
          try {
            if (pastedModel) {
              addWindow(
                'Import Diagram',
                <ImportForm importedData={pastedModel} />,
                {
                  x: 75,
                  y: 25,
                  width: 1300,
                  height: 750,
                },
              );
            }
          } catch (readError) {
            console.error('Error reading from clipboard:', readError);
            showAlert(
              'Failed to read from clipboard. Please try again.',
              'error',
            );
          }
        } else {
          addWindow('New Diagram', <DiagramForm />);
        }

        break;
      }
      case 'Logic Tree': {
        addWindow(
          'New Logic Tree',
          <LogicNodeFormContextProvider>
            <LogicNodeForm setAsRoot fromSidebar={true} />
          </LogicNodeFormContextProvider>,
        );

        break;
      }
      case 'External Sims': {
        addWindow('New External Sim', <ExtSimForm />);

        break;
      }
      case 'Actions': {
        addWindow(
          'New Action',
          <ActionFormContextProvider>
            <ActionForm />
          </ActionFormContextProvider>,
        );

        break;
      }
      case 'Events': {
        addWindow(
          'New Event',
          <EventFormContextProvider>
            <EventForm />
          </EventFormContextProvider>,
        );

        break;
      }
      case 'Variables': {
        addWindow(
          'New Variable',
          <VariableFormContextProvider>
            <VariableForm />
          </VariableFormContextProvider>,
        );

        break;
      }
      // No default
    }

    handleClose();
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <>
      <List>
        {panels.map((panel, p) => (
          <Accordion
            key={panel.type}
            expanded={expandedPanel === panel.type}
            onChange={handleChange(panel.type)}
          >
            <AccordionSummary
              aria-controls={`panel-${p.toString()}-content`}
              id={panel.type}
              onContextMenu={e =>
                void handleAccordionContextMenu(e, panel.type)}
            >
              <Typography>{panel.type}</Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 0 }}>
              <AccordionMenuItems
                item={panel}
                bothAccordionsOpen={bothAccordionsOpen}
                onDiagramChange={onDiagramChange}
                handleDelete={handleDelete}
              />
            </AccordionDetails>
          </Accordion>
        ))}
      </List>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
        <MenuItem
          onClick={e => {
            handleMenuItemClick(e);
          }}
          disabled={accordionPanel === 'Diagrams' && notValidModel}
        >
          {accordionPanel === 'Diagrams' && 'Paste'}
          {accordionPanel === 'Logic Tree' && 'New TOP Node'}
          {accordionPanel === 'External Sims' && 'New External Sim'}
          {accordionPanel === 'Actions' && 'New Action'}
          {accordionPanel === 'Events' && 'New Event'}
          {accordionPanel === 'Variables' && 'New Variable'}
        </MenuItem>
        {accordionPanel === 'Diagrams' && (
          <MenuItem
            onClick={e => {
              handleMenuItemClick(e);
            }}
          >
            New Diagram
          </MenuItem>
        )}
      </Menu>
    </>
  );
};
